import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import type { Doc, Id } from "../../convex/_generated/dataModel";

type Vehicle = Doc<"vehicles">;
type VehicleId = Id<"vehicles">;

function formatVehicleTitle(raw: unknown) {
  // Ensure names render on a single line in UI (no embedded newlines / excessive whitespace).
  const normalized = String(raw ?? "")
    .replace(/\s+/g, " ")
    .trim();
  // If the name ends with a number (e.g. "Patrol Vehicle 10"), prevent wrapping between
  // the last word and the trailing number without truncating the name.
  return normalized.replace(/\s+(\d+)\s*$/u, "\u00A0$1");
}

function sortByOrderThenName(a: Vehicle, b: Vehicle) {
  const ao = typeof a.order === "number" ? a.order : Number.POSITIVE_INFINITY;
  const bo = typeof b.order === "number" ? b.order : Number.POSITIVE_INFINITY;
  if (ao !== bo) return ao - bo;
  return (a.displayName || a.vehicleId || "").localeCompare(b.displayName || b.vehicleId || "");
}

export default function VehicleAdminSettings() {
  const vehicles = useQuery(api.vehicles.getAll) || [];
  const setAdminFields = useMutation(api.vehicles.setAdminFields);
  const setOrderBatch = useMutation(api.vehicles.setOrderBatch);

  const [activeView, setActiveView] = useState<"manage" | "order">("manage");

  const [globalBusy, setGlobalBusy] = useState(false);
  const [rowBusyIds, setRowBusyIds] = useState<Set<string>>(() => new Set());

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [showBlacklisted, setShowBlacklisted] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState("");
  const draftInputRef = useRef<HTMLInputElement | null>(null);

  const [orderDraftIds, setOrderDraftIds] = useState<VehicleId[]>([]);
  const [orderDirty, setOrderDirty] = useState(false);

  const orderedVehicles = useMemo(() => [...vehicles].sort(sortByOrderThenName), [vehicles]);

  const setFlash = (msg: string) => {
    setSuccess(msg);
    window.setTimeout(() => setSuccess(null), 2000);
  };

  const startRowBusy = (id: string) => setRowBusyIds((prev) => new Set(prev).add(id));
  const stopRowBusy = (id: string) =>
    setRowBusyIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });

  useEffect(() => {
    if (editingId && draftInputRef.current) {
      draftInputRef.current.focus();
      draftInputRef.current.select();
    }
  }, [editingId]);

  // Keep order editor in sync with server order unless the user has started editing.
  useEffect(() => {
    if (orderDirty) return;
    setOrderDraftIds(orderedVehicles.map((v) => v._id));
  }, [orderedVehicles, orderDirty]);

  const beginEdit = (v: Vehicle) => {
    setError(null);
    setEditingId(v._id);
    setDraftName(v.displayName || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraftName("");
  };

  const saveName = async (v: Vehicle) => {
    setError(null);
    const nextName = (draftName || "").trim();
    const finalName = nextName.length > 0 ? nextName : v.name || v.vehicleId;

    startRowBusy(v._id);
    try {
      await setAdminFields({ id: v._id, displayName: finalName });
      setFlash("Name saved");
      cancelEdit();
    } catch (e: any) {
      setError(`VEH-MGMT-001: ${e?.message || "Failed to save vehicle name"}`);
    } finally {
      stopRowBusy(v._id);
    }
  };

  const toggleBlacklist = async (v: Vehicle) => {
    setError(null);
    const next = !v.blacklisted;
    if (next) {
      const ok = window.confirm(
        `Blacklist “${v.displayName || v.vehicleId}”? This hides it across the app (Vehicles tab + Map).`
      );
      if (!ok) return;
    } else {
      const ok = window.confirm(`Remove “${v.displayName || v.vehicleId}” from blacklist?`);
      if (!ok) return;
    }

    startRowBusy(v._id);
    try {
      await setAdminFields({ id: v._id, blacklisted: next });
      setFlash(next ? "Blacklisted" : "Restored");
    } catch (e: any) {
      setError(`VEH-MGMT-003: ${e?.message || "Failed to update blacklist"}`);
    } finally {
      stopRowBusy(v._id);
    }
  };

  const moveOrderDraft = (id: VehicleId, direction: -1 | 1) => {
    setOrderDraftIds((prev) => {
      const idx = prev.findIndex((x) => x === id);
      if (idx < 0) return prev;
      const nextIdx = idx + direction;
      if (nextIdx < 0 || nextIdx >= prev.length) return prev;
      const copy = [...prev];
      [copy[idx], copy[nextIdx]] = [copy[nextIdx], copy[idx]];
      return copy;
    });
    setOrderDirty(true);
  };

  const saveOrder = async () => {
    setError(null);
    setGlobalBusy(true);
    try {
      await setOrderBatch({ orderedIds: orderDraftIds });
      setFlash("Order saved");
      setOrderDirty(false);
    } catch (e: any) {
      setError(`VEH-MGMT-004: ${e?.message || "Failed to save order"}`);
    } finally {
      setGlobalBusy(false);
    }
  };

  const resetOrder = () => {
    setOrderDraftIds(orderedVehicles.map((v) => v._id));
    setOrderDirty(false);
  };

  const filtered = useMemo(() => {
    const base = showBlacklisted ? orderedVehicles : orderedVehicles.filter((v) => !v.blacklisted);
    return base;
  }, [orderedVehicles, showBlacklisted]);

  return (
    <div className="vehicle-admin">
      {error && <div className="vehicle-admin-banner vehicle-admin-banner-error">{error}</div>}
      {success && <div className="vehicle-admin-banner vehicle-admin-banner-ok">{success}</div>}

      <div className="vehicle-admin-top">
        <div className="vehicle-admin-tabs" role="tablist" aria-label="Vehicle Management Views">
          <button
            type="button"
            className={`vehicle-admin-tab ${activeView === "manage" ? "active" : ""}`}
            onClick={() => setActiveView("manage")}
            role="tab"
            aria-selected={activeView === "manage"}
          >
            Manage
          </button>
          <button
            type="button"
            className={`vehicle-admin-tab ${activeView === "order" ? "active" : ""}`}
            onClick={() => setActiveView("order")}
            role="tab"
            aria-selected={activeView === "order"}
          >
            Order
          </button>
        </div>

        <div className="vehicle-admin-toolbar">
          <label className="vehicle-admin-toggle">
            <input
              type="checkbox"
              checked={showBlacklisted}
              onChange={(e) => setShowBlacklisted(e.target.checked)}
            />
            <span>Show blacklisted</span>
          </label>
          <div className="vehicle-admin-meta">
            <span className="vehicle-admin-count">{filtered.length}</span>
            <span className="vehicle-admin-count-suffix">shown</span>
          </div>
        </div>
      </div>

      {activeView === "manage" ? (
        <div className="vehicle-admin-table" role="list">
          {filtered.map((v) => {
            const isBusy = globalBusy || rowBusyIds.has(v._id);
            const isEditing = editingId === v._id;
            const title = formatVehicleTitle(v.displayName || v.vehicleId);

            return (
              <div
                key={v._id}
                className={`vehicle-admin-row ${v.blacklisted ? "blacklisted" : ""}`}
                role="listitem"
              >
                <div className="vehicle-admin-row-main">
                  <div className="vehicle-admin-row-title" title={title}>
                    <span className={`vehicle-admin-dot ${v.isMoving ? "moving" : "stopped"}`} />
                    <span className="vehicle-admin-title-text">{title}</span>
                    {v.blacklisted ? <span className="vehicle-admin-pill vehicle-admin-pill-danger">Blacklisted</span> : null}
                  </div>
                  <div className="vehicle-admin-row-sub">
                    <span className="vehicle-admin-sub-item">ID: {v.vehicleId}</span>
                  </div>
                </div>

                <div className="vehicle-admin-row-actions">
                  <button
                    type="button"
                    className={`vehicle-admin-action ${v.blacklisted ? "danger" : ""}`}
                    disabled={isBusy}
                    onClick={() => void toggleBlacklist(v)}
                    title={v.blacklisted ? "Remove from blacklist" : "Blacklist vehicle"}
                  >
                    {v.blacklisted ? "Restore" : "Blacklist"}
                  </button>

                  {isEditing ? (
                    <div className="vehicle-admin-edit">
                      <input
                        ref={draftInputRef}
                        className="vehicle-admin-edit-input"
                        value={draftName}
                        onChange={(e) => setDraftName(e.target.value)}
                        placeholder={`Defaults to: ${v.name || v.vehicleId}`}
                        disabled={isBusy}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") void saveName(v);
                          if (e.key === "Escape") cancelEdit();
                        }}
                      />
                      <button
                        type="button"
                        className="vehicle-admin-action accent"
                        disabled={isBusy}
                        onClick={() => void saveName(v)}
                      >
                        Save
                      </button>
                      <button type="button" className="vehicle-admin-action" disabled={isBusy} onClick={cancelEdit}>
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="vehicle-admin-action"
                      disabled={isBusy || !!v.blacklisted}
                      onClick={() => beginEdit(v)}
                      title={v.blacklisted ? "Restore to edit name" : "Edit display name"}
                    >
                      Rename
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          {filtered.length === 0 ? <div className="vehicle-admin-empty">No vehicles match your filters.</div> : null}
        </div>
      ) : (
        <div className="vehicle-admin-order">
          <div className="vehicle-admin-order-actions">
            <button type="button" className="vehicle-admin-action accent" disabled={globalBusy || !orderDirty} onClick={() => void saveOrder()}>
              Save order
            </button>
            <button type="button" className="vehicle-admin-action" disabled={globalBusy || !orderDirty} onClick={resetOrder}>
              Reset
            </button>
            {orderDirty ? <span className="vehicle-admin-order-dirty">Unsaved changes</span> : null}
          </div>

          <div className="vehicle-admin-order-list">
            {orderDraftIds.map((id, idx) => {
              const v = orderedVehicles.find((x) => x._id === id);
              if (!v) return null;
              const isBusy = globalBusy;

              return (
                <div key={v._id} className={`vehicle-admin-order-row ${v.blacklisted ? "blacklisted" : ""}`}>
                  <div className="vehicle-admin-order-left">
                    <span className="vehicle-admin-order-index">{idx + 1}</span>
                    <div className="vehicle-admin-order-info">
                      <div className="vehicle-admin-order-title">{formatVehicleTitle(v.displayName || v.vehicleId)}</div>
                      <div className="vehicle-admin-order-sub">{v.vehicleId}</div>
                    </div>
                  </div>

                  <div className="vehicle-admin-order-buttons">
                    <button
                      type="button"
                      className="vehicle-admin-btn"
                      disabled={isBusy || idx === 0}
                      onClick={() => moveOrderDraft(v._id, -1)}
                      title="Move up"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M7 14l5-5 5 5z" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="vehicle-admin-btn"
                      disabled={isBusy || idx === orderDraftIds.length - 1}
                      onClick={() => moveOrderDraft(v._id, 1)}
                      title="Move down"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M7 10l5 5 5-5z" />
                      </svg>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

